// InviteCollabbayCreator.js
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  Image,
  TouchableOpacity,
  ScrollView,
  Alert,
  StyleSheet,
  ActivityIndicator,
  Modal,
  SafeAreaView,
  useWindowDimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Calendar } from 'react-native-calendars';
import { Linking } from 'react-native';
import { useRoute, useNavigation } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';

// Half-hour slots from 10:00 to 21:30
const TIME_SLOTS = Array.from({ length: 24 }, (_, i) => {
  const hour = Math.floor(i / 2) + 10;
  const minute = i % 2 === 0 ? '00' : '30';
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const adjustedHour = hour > 12 ? hour - 12 : hour;
  return `${adjustedHour}:${minute} ${ampm}`;
});

const InviteCollabbayCreator = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { Username } = route.params;

  const { width } = useWindowDimensions();
  const isLargeLayout = width >= 1024;

  const [creator, setCreator] = useState(null);
  const [offers, setOffers] = useState([]);
  const [selectedListingID, setSelectedListingID] = useState('');
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState(TIME_SLOTS[2]); // e.g. 11:00 AM
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [addedVisitors, setAddedVisitors] = useState('0');
  const [infoModalVisible, setInfoModalVisible] = useState(false);
  const [timePickerVisible, setTimePickerVisible] = useState(false);

  // NEW: user + subscription state for sidenav
  const [userData, setUserData] = useState({});
  const [isAuthed, setIsAuthed] = useState(false);
  const [authMode, setAuthMode] = useState('none'); // "subscriber" | "none"
  const [authChecked, setAuthChecked] = useState(false);
  const isSubscriber = authMode === 'subscriber';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [resCreator, resOffers] = await Promise.all([
          fetch(
            `https://collabbayadmin.com/APIControllers/ShowPromoterProfile.php?Promoter=${Username}`
          ),
          fetch(
            'https://collabbayadmin.com/APIControllers/RetrieveBrandListings.php',
            { credentials: 'include' }
          ),
        ]);

        const creatorData = await resCreator.json();
        setCreator(creatorData[0]);

        const offersData = await resOffers.json();
        const liveOffers = offersData.filter(
          (offer) => offer.PauseLiveToggle === 'Live'
        );
        setOffers(liveOffers);
      } catch (err) {
        console.error(err);
        Alert.alert('Error', 'Could not load creator or offers.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [Username]);

  // Fetch session user for sidenav username pill
  useEffect(() => {
    fetch('https://collabbayadmin.com/APIControllers/GetSessionData.php', {
      credentials: 'include',
    })
      .then((response) => response.json())
      .then((data) => {
        setUserData(data);
      })
      .catch((error) => {
        console.error('Error fetching user data:', error);
      });
  }, []);

  // Fetch subscription/auth state for locking Activity + Calendar
  useEffect(() => {
    fetch('https://collabbayadmin.com/APIControllers/UserSubscriptionAuth.php', {
      method: 'GET',
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) => {
        const authed =
          typeof data.authenticated === 'boolean'
            ? data.authenticated
            : data.status === 'active';
        const mode = data.auth_mode
          ? data.auth_mode
          : data.status === 'active'
          ? 'subscriber'
          : 'none';

        setIsAuthed(!!authed);
        setAuthMode(mode);
      })
      .catch((err) => console.error('Error fetching auth state:', err))
      .finally(() => setAuthChecked(true));
  }, []);

  const openInSameTab = (url) => {
    if (typeof window !== 'undefined') {
      window.location.href = url;
    } else {
      Linking.openURL(url);
    }
  };

  const calculateAge = (dob) => {
    if (!dob) return 'N/A';
    const birth = new Date(dob);
    const now = new Date();
    let age = now.getFullYear() - birth.getFullYear();
    const m = now.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
    return age;
  };

  const handleOfferSubmit = async () => {
    if (!selectedOffer || !selectedDate || !selectedTime) {
      Alert.alert('Missing details', 'Please select an offer, date and time.');
      return;
    }

    setIsSubmitting(true);

    const formattedDate = `${selectedDate} ${selectedTime}`;

    const payload = {
      Promoter: Username,
      ListingID: selectedOffer.ListingID,
      CreditAvailableStart: formattedDate,
      AddedVisitors: addedVisitors,
    };

    try {
      const res = await fetch(
        'https://collabbayadmin.com/APIControllers/InsertVenueCollabSubmission.php',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload),
        }
      );
      const result = await res.json();
      if (result.success) {
        setSuccessModalVisible(true);
        setTimeout(() => {
          Linking.openURL('https://collabbayadmin.com');
        }, 1500);
      } else {
        Alert.alert('Error', result.error || 'Submission failed');
      }
    } catch (err) {
      console.error(err);
      Alert.alert('Error', 'Submission failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  // LOADING STATE — shell without sidenav content
  if (loading || !creator) {
    return (
      <View style={styles.root}>
        <SafeAreaView style={styles.safeArea}>
          <View style={styles.shellRow}>
            <View style={styles.mainArea}>
              <View style={[styles.loaderShell, { flex: 1 }]}>
                <ActivityIndicator size="large" color="#111827" />
                <Text style={styles.loaderText}>
                  Loading creator & offers…
                </Text>
              </View>
            </View>
          </View>
        </SafeAreaView>
      </View>
    );
  }

  return (
    <View style={styles.root}>
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.shellRow}>
          {/* SIDE NAV — EXACT SAME AS BrandCalendar */}
          <View style={styles.sideNav}>
            <LinearGradient
              colors={['#050509', '#050505', '#000000']}
              style={styles.sideNavInner}
            >
              {/* subtle glow blob */}
              <LinearGradient
                colors={['rgba(253,240,93,0.08)', 'rgba(0,0,0,0)']}
                style={styles.sideNavGlow}
                start={{ x: 0.1, y: 0 }}
                end={{ x: 1, y: 1 }}
              />

              {/* TOP: Logo + wordmark */}
              <View style={styles.sideNavHeader}>
                <Image
                  source={{
                    uri: 'https://collabbayadmin.com/CollabbayLogoDark.png',
                  }}
                  style={styles.sideNavLogoImage}
                  resizeMode="contain"
                />
                <View>
                  <Text style={styles.logoWordmark}>
                    <Text style={{ fontWeight: '300' }}>collab</Text>
                    bay
                    <Text style={styles.logoDot}>.</Text>
                  </Text>
                  <Text style={styles.logoDesktopTag}>DESKTOP</Text>
                </View>
              </View>

              {/* MAIN NAV SECTION */}
              <View style={styles.sideNavSection}>
                {/* Username pill */}
                <LinearGradient
                  colors={['#111111', '#2b2b2b']}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 0 }}
                  style={styles.usernamePill}
                >
                  <Text
                    numberOfLines={1}
                    ellipsizeMode="tail"
                    style={styles.usernamePillText}
                  >
                    {userData.Username ? ` ${userData.Username}` : 'Welcome Back'}
                  </Text>
                </LinearGradient>

                {/* Home → MyPanel */}
                <TouchableOpacity
                  style={styles.navItem}
                  onPress={() => navigation.navigate('MyPanel')}
                >
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons
                        name="home-outline"
                        size={20}
                        color="#f5f5f5"
                        style={styles.navIcon}
                      />
                      <Text style={styles.navText}>Home</Text>
                    </View>
                  </View>
                </TouchableOpacity>

                {/* My Offers → MyOffers */}
                <TouchableOpacity
                  style={styles.navItem}
                  onPress={() => navigation.navigate('MyOffers')}
                >
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons
                        name="pricetags-outline"
                        size={20}
                        color="#f5f5f5"
                        style={styles.navIcon}
                      />
                      <Text style={styles.navText}>My Offers</Text>
                    </View>
                  </View>
                </TouchableOpacity>

                {/* My Calendar → MyCalendar (LOCKED if NOT authed) */}
                <TouchableOpacity
                  style={styles.navItem}
                  onPress={() => {
                    if (authChecked && !isAuthed) {
                      openInSameTab('https://collabbayadmin.com/subscribe');
                    } else {
                      navigation.navigate('MyCalendar');
                    }
                  }}
                >
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons
                        name="calendar-outline"
                        size={20}
                        color="#f5f5f5"
                        style={styles.navIcon}
                      />
                      <Text style={styles.navText}>My Calendar</Text>
                    </View>
                    {authChecked && !isAuthed && (
                      <Ionicons
                        name="lock-closed-outline"
                        size={16}
                        color="#FDF05D"
                        style={styles.lockIcon}
                      />
                    )}
                  </View>
                </TouchableOpacity>

                {/* Browse Creators → BrowseCreators */}
                <TouchableOpacity
                  style={styles.navItem}
                  onPress={() => navigation.navigate('BrowseCreators')}
                >
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons
                        name="people-outline"
                        size={20}
                        color="#f5f5f5"
                        style={styles.navIcon}
                      />
                      <Text style={styles.navText}>Browse Creators</Text>
                    </View>
                  </View>
                </TouchableOpacity>

                {/* Activity → ActivityFeed (LOCKED if NOT authed) */}
                <TouchableOpacity
                  style={styles.navItem}
                  onPress={() => {
                    if (authChecked && !isAuthed) {
                      openInSameTab('https://collabbayadmin.com/subscribe');
                    } else {
                      navigation.navigate('ActivityFeed');
                    }
                  }}
                >
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons
                        name="chatbubbles-outline"
                        size={20}
                        color="#f5f5f5"
                        style={styles.navIcon}
                      />
                      <Text style={styles.navText}>Activity</Text>
                    </View>
                    {authChecked && !isAuthed && (
                      <Ionicons
                        name="lock-closed-outline"
                        size={16}
                        color="#FDF05D"
                        style={styles.lockIcon}
                      />
                    )}
                  </View>
                </TouchableOpacity>

                {/* Account → BusinessAccountPage */}
                <TouchableOpacity
                  style={styles.navItem}
                  onPress={() => navigation.navigate('BusinessAccountPage')}
                >
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons
                        name="person-circle-outline"
                        size={20}
                        color="#f5f5f5"
                        style={styles.navIcon}
                      />
                      <Text style={styles.navText}>Account</Text>
                    </View>
                  </View>
                </TouchableOpacity>
              </View>

              {/* MIDDLE SECTION: Peer Map + Subscribe CTA */}
              <View style={styles.sideNavSection}>
                {/* Peer Map */}
                <TouchableOpacity
                  style={styles.peerMapCard}
                  activeOpacity={0.9}
                  onPress={() => openInSameTab('/venues')}
                >
                  <LinearGradient
                    colors={['rgba(255,255,255,0.08)', 'rgba(0,0,0,0.4)']}
                    style={styles.peerMapInner}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                  >
                    <View style={styles.peerMapContent}>
                      <Ionicons
                        name="map-outline"
                        size={18}
                        color="#FDF05D"
                        style={{ marginRight: 8 }}
                      />
                      <View style={{ flex: 1 }}>
                        <Text style={styles.peerMapTitle}>Peer Map</Text>
                        <Text style={styles.peerMapSubtitle}>
                          View 100+ venues on Collabbay
                        </Text>
                      </View>
                    </View>
                  </LinearGradient>
                </TouchableOpacity>

                {/* Subscribe CTA — only if NOT subscriber */}
                {!isSubscriber && (
                  <TouchableOpacity
                    style={styles.subscribeCard}
                    activeOpacity={0.9}
                    onPress={() =>
                      openInSameTab('https://collabbayadmin.com/subscribe')
                    }
                  >
                    <LinearGradient
                      colors={['#FDF05D', '#fbbf24']}
                      style={styles.subscribeGradient}
                      start={{ x: 0, y: 0 }}
                      end={{ x: 1, y: 1 }}
                    >
                      <View style={styles.subscribeContent}>
                        <Ionicons
                          name="rocket-outline"
                          size={20}
                          color="#000"
                          style={{ marginRight: 8 }}
                        />
                        <View style={{ flex: 1 }}>
                          <Text style={styles.subscribeTitle}>
                            Subscribe & unlock
                          </Text>
                          <Text style={styles.subscribeSubtitle}>
                            Unlock Activity & Calendar, plus more.
                          </Text>
                        </View>
                      </View>
                    </LinearGradient>
                  </TouchableOpacity>
                )}
              </View>

              {/* BOTTOM: FAQ + Help */}
              <View style={styles.sideNavBottom}>
                <TouchableOpacity
                  style={styles.bottomLink}
                  onPress={() =>
                    openInSameTab('https://collabbayadmin.com/FAQs')
                  }
                >
                  <Ionicons
                    name="help-circle-outline"
                    size={18}
                    color="#f5f5f5"
                    style={{ marginRight: 8 }}
                  />
                  <View>
                    <Text style={styles.bottomLinkText}>FAQs</Text>
                    <Text style={styles.bottomLinkCaption}>
                      Common questions answered
                    </Text>
                  </View>
                </TouchableOpacity>

                <TouchableOpacity
                  style={styles.bottomLink}
                  onPress={() =>
                    Linking.openURL('mailto:info@collab-bay.com')
                  }
                >
                  <Ionicons
                    name="headset-outline"
                    size={18}
                    color="#f5f5f5"
                    style={{ marginRight: 8 }}
                  />
                  <View>
                    <Text style={styles.bottomLinkText}>Help</Text>
                    <Text style={styles.bottomLinkCaption}>
                      We&apos;re here 24/7
                    </Text>
                  </View>
                </TouchableOpacity>
              </View>
            </LinearGradient>
          </View>

          {/* MAIN AREA */}
          <View style={styles.mainArea}>
            <ScrollView
              contentContainerStyle={styles.scrollContainer}
              showsVerticalScrollIndicator={false}
            >
              <View style={styles.contentWrapper}>
                {/* Header Row */}
                <View style={styles.pageHeaderRow}>
                  <TouchableOpacity
                    onPress={() => navigation.goBack()}
                    style={styles.backButton}
                  >
                    <Ionicons name="arrow-back" size={20} color="black" />
                    <Text style={styles.backButtonText}>Back</Text>
                  </TouchableOpacity>
                </View>

                <Text style={styles.pageTitle}>
                  Invite @{creator.PlatformUsername}
                </Text>

                {/* THREE COLUMN LAYOUT */}
                <View
                  style={[
                    styles.columnsRow,
                    !isLargeLayout && styles.columnsStacked,
                  ]}
                >
                  {/* LEFT: CREATOR PROFILE & OFFER CTA */}
                  <View
                    style={[
                      styles.col,
                      styles.leftCol,
                      !isLargeLayout && styles.colFullWidth,
                    ]}
                  >
                    <View style={styles.profileCardLarge}>
                      <View style={styles.profileRowModern}>
                        <View style={styles.leftColumnModern}>
                          <Image
                            source={{ uri: creator.ProfileImage }}
                            style={styles.profileImage}
                          />
                          <Text style={styles.username}>
                            {creator.PlatformUsername}
                          </Text>
                        </View>
                        <View style={styles.rightColumnModern}>
                          <Text style={styles.bioText}>
                            <Ionicons name="people-outline" size={16} />{' '}
                            {creator.PlatformFollowers} Followers
                          </Text>
                          <Text style={styles.bioText}>
                            <Ionicons
                              name="information-circle-outline"
                              size={16}
                            />{' '}
                            {creator.ContentCategories} Creator
                          </Text>
                          <Text style={styles.bioText}>
                            <Ionicons name="calendar-outline" size={16} /> Age:{' '}
                            {calculateAge(creator.DOB)}
                          </Text>
                          <TouchableOpacity
                            style={styles.profileLink}
                            onPress={() =>
                              Linking.openURL(
                                `https://instagram.com/${creator.PlatformUsername}`
                              )
                            }
                          >
                            <Text style={styles.linkText}>
                              View Instagram profile
                            </Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    </View>

                    <TouchableOpacity
                      onPress={() => setModalVisible(true)}
                      style={styles.openModalBtn}
                    >
                      <Text style={styles.modalBtnText}>
                        📦 Tap here to select an offer to propose
                      </Text>
                    </TouchableOpacity>
                  </View>

                  {/* MIDDLE: CALENDAR + TIME DROPDOWN */}
                  <View
                    style={[
                      styles.col,
                      styles.middleCol,
                      !isLargeLayout && styles.colFullWidth,
                    ]}
                  >
                    <View style={styles.calendarCard}>
                      <Text style={styles.sectionTitle}>
                        📅 Choose a day to invite the creator
                      </Text>
                      <Calendar
                        onDayPress={(day) => setSelectedDate(day.dateString)}
                        markedDates={{
                          [selectedDate]: {
                            selected: true,
                            selectedColor: '#FDF05D',
                          },
                        }}
                        theme={{
                          todayTextColor: '#FDF05D',
                          selectedDayTextColor: '#000',
                        }}
                        style={{ marginTop: 8, borderRadius: 12 }}
                      />
                      <Text style={styles.timeLabel}>Select a time</Text>
                      <View style={styles.timeDropdownWrapper}>
                        <TouchableOpacity
                          style={styles.timeDropdown}
                          onPress={() => setTimePickerVisible(true)}
                        >
                          <Text style={styles.timeDropdownText}>
                            {selectedTime || 'Select a time'}
                          </Text>
                          <Ionicons
                            name="chevron-down"
                            size={16}
                            color="#555"
                          />
                        </TouchableOpacity>
                      </View>
                    </View>
                  </View>

                  {/* RIGHT: RECAP + GUESTS + CONFIRM */}
                  <View
                    style={[
                      styles.col,
                      styles.rightCol,
                      !isLargeLayout && styles.colFullWidth,
                    ]}
                  >
                    <View style={styles.recapCard}>
                      <Text style={styles.sectionTitle}>Recap & confirm</Text>

                      <View style={styles.recapBlock}>
                        <Text style={styles.recapLabel}>Creator</Text>
                        <Text style={styles.recapValue}>
                          @{creator.PlatformUsername}
                        </Text>
                      </View>

                      <View style={styles.recapBlock}>
                        <Text style={styles.recapLabel}>Offer</Text>
                        {selectedOffer ? (
                          <>
                            <Text style={styles.recapValue}>
                              {selectedOffer.NonCreditOffer}
                            </Text>
                            <Text style={styles.recapSubValue}>
                              Value: £{selectedOffer.Credits}
                            </Text>
                          </>
                        ) : (
                          <Text style={styles.recapPlaceholder}>
                            No offer selected yet
                          </Text>
                        )}
                      </View>

                      <View style={styles.recapBlock}>
                        <Text style={styles.recapLabel}>Date & time</Text>
                        {selectedDate ? (
                          <Text style={styles.recapValue}>
                            {selectedDate} · {selectedTime}
                          </Text>
                        ) : (
                          <Text style={styles.recapPlaceholder}>
                            Pick a date & time
                          </Text>
                        )}
                      </View>

                      <View style={styles.recapBlock}>
                        <View
                          style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: 4,
                          }}
                        >
                          <Text style={styles.recapLabel}>
                            Guests (plus-ones)
                          </Text>
                          <TouchableOpacity
                            onPress={() => setInfoModalVisible(true)}
                          >
                            <Ionicons
                              name="information-circle-outline"
                              size={18}
                              color="#888"
                            />
                          </TouchableOpacity>
                        </View>

                        <Text style={styles.guestsSubtitle}>
                          Allowing at least 1 plus-one is recommended.
                        </Text>

                        <ScrollView
                          horizontal
                          showsHorizontalScrollIndicator={false}
                          contentContainerStyle={{ paddingVertical: 4 }}
                        >
                          {[
                            { label: 'Just the creator', value: '0' },
                            { label: '+1', value: '1' },
                            { label: '+2', value: '2' },
                            { label: '+3', value: '3' },
                            { label: '+4', value: '4' },
                          ].map((option) => (
                            <TouchableOpacity
                              key={option.value}
                              onPress={() => setAddedVisitors(option.value)}
                              style={[
                                styles.guestOptionBtn,
                                addedVisitors === option.value &&
                                  styles.guestOptionSelected,
                              ]}
                            >
                              <Text style={styles.guestOptionText}>
                                {option.label}
                              </Text>
                            </TouchableOpacity>
                          ))}
                        </ScrollView>
                      </View>

                      <TouchableOpacity
                        style={[
                          styles.submitBtn,
                          isSubmitting && { opacity: 0.5 },
                        ]}
                        onPress={handleOfferSubmit}
                        disabled={isSubmitting}
                      >
                        <Text style={styles.submitBtnText}>
                          {isSubmitting ? 'Submitting...' : 'Propose invite'}
                        </Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                </View>
              </View>
            </ScrollView>
          </View>
        </View>
      </SafeAreaView>

      {/* Offer selection modal */}
      <Modal visible={modalVisible} animationType="slide">
        <SafeAreaView style={{ flex: 1, backgroundColor: '#fff' }}>
          <View style={styles.modalView}>
            <View style={styles.modalHeaderRow}>
              <Text style={styles.modalHeader}>Choose an offer</Text>
              <TouchableOpacity onPress={() => setModalVisible(false)}>
                <Ionicons name="close" size={28} color="#333" />
              </TouchableOpacity>
            </View>
            <ScrollView showsVerticalScrollIndicator={false}>
              {offers.map((offer, i) => (
                <TouchableOpacity
                  key={i}
                  style={[
                    styles.modalOfferCard,
                    selectedListingID === offer.ListingID &&
                      styles.modalOfferCardSelected,
                  ]}
                  onPress={() => {
                    setSelectedListingID(offer.ListingID);
                    setSelectedOffer(offer);
                    setModalVisible(false);
                  }}
                >
                  <Text style={styles.modalOfferTitle}>
                    {offer.NonCreditOffer}
                  </Text>
                  <Text style={styles.modalOfferValue}>
                    Value: £{offer.Credits}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </SafeAreaView>
      </Modal>

      {/* Time picker modal */}
      <Modal
        visible={timePickerVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setTimePickerVisible(false)}
      >
        <View style={styles.modalBackdrop}>
          <View style={styles.timeModalContent}>
            <View style={styles.timeModalHeaderRow}>
              <Text style={styles.timeModalTitle}>Select a time</Text>
              <TouchableOpacity onPress={() => setTimePickerVisible(false)}>
                <Ionicons name="close" size={24} color="#333" />
              </TouchableOpacity>
            </View>
            <ScrollView
              style={{ maxHeight: 320, width: '100%' }}
              showsVerticalScrollIndicator
            >
              {TIME_SLOTS.map((time) => (
                <TouchableOpacity
                  key={time}
                  style={[
                    styles.timeOptionRow,
                    selectedTime === time && styles.timeOptionRowSelected,
                  ]}
                  onPress={() => {
                    setSelectedTime(time);
                    setTimePickerVisible(false);
                  }}
                >
                  <Text
                    style={[
                      styles.timeOptionRowText,
                      selectedTime === time &&
                        styles.timeOptionRowTextSelected,
                    ]}
                  >
                    {time}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Guests info modal */}
      <Modal
        visible={infoModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setInfoModalVisible(false)}
      >
        <View style={styles.modalBackdrop}>
          <View style={styles.infoModalContent}>
            <Ionicons
              name="information-circle-outline"
              size={32}
              color="#fdf05d"
            />
            <Text style={styles.infoText}>
              Allowing creators to bring guests can help them feel more
              comfortable and increase their likelihood of attending.{'\n'}
              The most common party size is 2, the creator and a plus-one.
              Only the influencer will be due to deliver your content in
              exchange for the offer.
            </Text>
            <TouchableOpacity
              style={styles.dismissButton}
              onPress={() => setInfoModalVisible(false)}
            >
              <Text style={styles.dismissButtonText}>Got it</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Success modal */}
      <Modal visible={successModalVisible} transparent>
        <View style={styles.successOverlay}>
          <View style={styles.successModalBox}>
            <Ionicons
              name="checkmark-circle-outline"
              size={48}
              color="green"
            />
            <Text style={styles.successText}>
              Request sent. You'll be notified if the creator accepts.
            </Text>
            <TouchableOpacity
              style={styles.submitBtn}
              onPress={() => setSuccessModalVisible(false)}
            >
              <Text style={styles.submitBtnText}>OK</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  // SHELL
  root: {
    flex: 1,
    backgroundColor: '#f3f4f6',
  },
  safeArea: {
    flex: 1,
  },
  shellRow: {
    flex: 1,
    flexDirection: 'row',
  },

  // MAIN AREA
  mainArea: {
    flex: 1,
    backgroundColor: '#f3f3f3',
  },
  scrollContainer: {
    paddingHorizontal: 24,
    paddingVertical: 24,
  },
  contentWrapper: {
    width: '100%',
    maxWidth: 1200,
    alignSelf: 'center',
  },

  // LOADER
  loaderShell: {
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f3f3f3',
  },
  loaderText: {
    marginTop: 8,
    color: '#555',
    fontSize: 14,
  },

  // PAGE HEADER
  pageHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#eee',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 999,
  },
  backButtonText: {
    marginLeft: 6,
    fontSize: 13,
    color: '#333',
    fontWeight: '500',
  },

  pageTitle: {
    fontSize: 20,
    fontWeight: '800',
    color: '#111',
    marginBottom: 18,
  },

  // COLUMNS
  columnsRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  columnsStacked: {
    flexDirection: 'column',
  },
  col: {
    flex: 1,
    marginHorizontal: 6,
  },
  leftCol: {
    maxWidth: 360,
  },
  middleCol: {
    maxWidth: 400,
  },
  rightCol: {
    maxWidth: 360,
  },
  colFullWidth: {
    maxWidth: '100%',
    marginBottom: 16,
  },

  // PROFILE CARD
  profileCardLarge: {
    backgroundColor: '#f9f9f9',
    padding: 18,
    borderRadius: 24,
    marginBottom: 14,
    borderWidth: 1,
    borderColor: '#ececec',
  },
  profileRowModern: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  leftColumnModern: {
    alignItems: 'center',
    width: '40%',
  },
  rightColumnModern: {
    width: '60%',
    justifyContent: 'center',
    paddingLeft: 20,
  },
  profileImage: {
    width: 120,
    height: 120,
    borderRadius: 24,
    borderColor: '#ddd',
    borderWidth: 1,
    backgroundColor: '#e5e5e5',
    resizeMode: 'cover',
  },
  username: {
    marginTop: 8,
    fontSize: 15,
    fontWeight: 'bold',
    color: '#000',
  },
  bioText: {
    fontSize: 14,
    color: '#333',
    marginBottom: 4,
    fontWeight: '600',
  },
  profileLink: {
    backgroundColor: '#fdf05d',
    paddingVertical: 7,
    paddingHorizontal: 10,
    borderRadius: 8,
    marginTop: 8,
    alignSelf: 'flex-start',
  },
  linkText: {
    fontWeight: 'bold',
    fontSize: 13,
    color: '#000',
  },

  openModalBtn: {
    backgroundColor: '#fdf05d',
    padding: 12,
    borderRadius: 12,
    marginTop: 4,
  },
  modalBtnText: {
    fontWeight: 'bold',
    textAlign: 'center',
    color: '#000',
  },

  // CALENDAR + TIME
  calendarCard: {
    backgroundColor: '#fff',
    borderRadius: 24,
    padding: 16,
    borderWidth: 1,
    borderColor: '#ececec',
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: '#111',
  },
  timeLabel: {
    marginTop: 12,
    marginBottom: 6,
    fontSize: 13,
    fontWeight: '600',
    color: '#444',
  },
  timeDropdownWrapper: {
    marginTop: 2,
  },
  timeDropdown: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#f9fafb',
    borderRadius: 12,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: '#d1d5db',
  },
  timeDropdownText: {
    fontSize: 14,
    color: '#111827',
    fontWeight: '500',
  },

  // RECAP CARD
  recapCard: {
    backgroundColor: '#fff',
    borderRadius: 24,
    padding: 16,
    borderWidth: 1,
    borderColor: '#ececec',
  },
  recapBlock: {
    marginTop: 10,
  },
  recapLabel: {
    fontSize: 12,
    color: '#6b7280',
    marginBottom: 2,
    fontWeight: '600',
  },
  recapValue: {
    fontSize: 14,
    color: '#111',
    fontWeight: '600',
  },
  recapSubValue: {
    fontSize: 12,
    color: '#444',
    marginTop: 2,
  },
  recapPlaceholder: {
    fontSize: 13,
    color: '#9ca3af',
  },

  guestsSubtitle: {
    fontSize: 12,
    color: '#666',
    marginTop: 2,
    marginBottom: 6,
  },
  guestOptionBtn: {
    paddingVertical: 8,
    paddingHorizontal: 14,
    backgroundColor: '#eee',
    borderRadius: 10,
    marginRight: 8,
    marginBottom: 8,
  },
  guestOptionSelected: {
    backgroundColor: '#fdf05d',
  },
  guestOptionText: {
    fontWeight: 'bold',
    color: '#000',
    fontSize: 12,
  },

  submitBtn: {
    backgroundColor: '#fdf05d',
    padding: 13,
    borderRadius: 12,
    marginTop: 18,
  },
  submitBtnText: {
    fontWeight: 'bold',
    textAlign: 'center',
    color: '#000',
    fontSize: 14,
  },

  // OFFER MODAL
  modalView: {
    padding: 20,
    flex: 1,
    backgroundColor: '#fff',
  },
  modalHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  modalHeader: {
    fontWeight: 'bold',
    fontSize: 18,
  },
  modalOfferCard: {
    padding: 14,
    marginVertical: 6,
    backgroundColor: '#f4f4f5',
    borderRadius: 10,
  },
  modalOfferCardSelected: {
    backgroundColor: '#fdf05d',
  },
  modalOfferTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: '#111',
  },
  modalOfferValue: {
    fontSize: 13,
    color: '#444',
    marginTop: 4,
  },

  // SHARED MODAL BACKDROP
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },

  // TIME MODAL
  timeModalContent: {
    backgroundColor: '#fff',
    padding: 18,
    borderRadius: 18,
    width: '80%',
    maxWidth: 360,
  },
  timeModalHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  timeModalTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#111827',
  },
  timeOptionRow: {
    paddingVertical: 10,
    paddingHorizontal: 10,
    borderRadius: 10,
    marginVertical: 2,
  },
  timeOptionRowSelected: {
    backgroundColor: '#fdf05d',
  },
  timeOptionRowText: {
    fontSize: 14,
    color: '#111827',
  },
  timeOptionRowTextSelected: {
    fontWeight: '700',
  },

  // INFO MODAL
  infoModalContent: {
    backgroundColor: '#fff',
    padding: 24,
    borderRadius: 20,
    width: '80%',
    maxWidth: 400,
    alignItems: 'center',
  },
  infoText: {
    fontSize: 15,
    textAlign: 'center',
    color: '#333',
    marginTop: 12,
    marginBottom: 20,
  },
  dismissButton: {
    backgroundColor: '#fdf05d',
    paddingVertical: 10,
    paddingHorizontal: 24,
    borderRadius: 10,
  },
  dismissButtonText: {
    fontWeight: 'bold',
    color: '#000',
  },

  // SUCCESS MODAL
  successOverlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.4)',
  },
  successModalBox: {
    backgroundColor: '#fff',
    padding: 20,
    borderRadius: 16,
    width: '80%',
    maxWidth: 420,
    alignItems: 'center',
  },
  successText: {
    fontSize: 16,
    marginVertical: 10,
    textAlign: 'center',
    color: '#111',
  },

  // SIDE NAV (EXACT FROM BrandCalendar)
  sideNav: {
    width: 250,
    backgroundColor: '#000',
  },
  sideNavInner: {
    flex: 1,
    paddingVertical: 24,
    paddingHorizontal: 16,
    borderTopRightRadius: 20,
    borderBottomRightRadius: 20,
    overflow: 'hidden',
    position: 'relative',
  },
  sideNavGlow: {
    position: 'absolute',
    top: 140,
    left: -60,
    width: 260,
    height: 260,
    borderRadius: 130,
    opacity: 0.55,
  },
  sideNavHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 26,
    zIndex: 2,
  },
  sideNavLogoImage: {
    width: 38,
    height: 38,
    marginRight: 10,
  },
  logoWordmark: {
    fontSize: 16,
    color: '#f5f5f5',
    fontWeight: '600',
    letterSpacing: 0.3,
  },
  logoDot: {
    color: '#FDF05D',
    fontSize: 18,
    fontWeight: '800',
  },
  logoDesktopTag: {
    fontSize: 9,
    color: '#b3b3b3',
    letterSpacing: 2,
    marginTop: 2,
  },
  sideNavSection: {
    marginTop: 12,
    marginBottom: 20,
    zIndex: 2,
  },
  usernamePill: {
    paddingTop: 4,
    paddingBottom: 8,
    paddingHorizontal: 10,
    borderRadius: 999,
    alignSelf: 'flex-start',
    maxWidth: '100%',
    marginLeft: 1,
    marginBottom: 10,
  },
  usernamePillText: {
    fontSize: 8,
    color: '#ffffff',
    textTransform: 'uppercase',
    letterSpacing: 1,
    textAlign: 'left',
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 9,
    paddingHorizontal: 8,
    borderRadius: 10,
    marginBottom: 6,
  },
  navIcon: {
    marginRight: 10,
  },
  navItemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flex: 1,
  },
  navItemMainLabel: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 1,
  },
  navText: {
    color: '#f5f5f5',
    fontSize: 14,
    fontWeight: '500',
  },
  lockIcon: {
    marginLeft: 6,
  },
  peerMapCard: {
    borderRadius: 14,
    overflow: 'hidden',
    marginTop: 4,
    marginBottom: 10,
  },
  peerMapInner: {
    paddingVertical: 10,
    paddingHorizontal: 10,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
  },
  peerMapContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  peerMapTitle: {
    color: '#f5f5f5',
    fontSize: 13,
    fontWeight: '600',
  },
  peerMapSubtitle: {
    color: '#b3b3b3',
    fontSize: 11,
    marginTop: 2,
  },
  subscribeCard: {
    borderRadius: 14,
    overflow: 'hidden',
    marginTop: 10,
  },
  subscribeGradient: {
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  subscribeContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  subscribeTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#000',
  },
  subscribeSubtitle: {
    fontSize: 11,
    color: '#1f2933',
    marginTop: 2,
  },
  sideNavBottom: {
    marginTop: 'auto',
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#181818',
    zIndex: 2,
  },
  bottomLink: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
  },
  bottomLinkText: {
    fontSize: 13,
    color: '#f5f5f5',
    fontWeight: '600',
  },
  bottomLinkCaption: {
    fontSize: 11,
    color: '#b3b3b3',
    marginTop: 2,
  },
});

export default InviteCollabbayCreator;
